<template>
    <div>
        <div v-if="!selectedCategories?.length">
            <offer-category-component
                :title="$t('pages.offer.create_offer.create_category')"
                :offer="offer"
                :allowSelection="true"
                :showFullDescription="true"
                @categories-select="onCategoriesSelect"
                @categories-change="onCategoriesChange"
            ></offer-category-component>
        </div>
        <common-calendar
            v-else
            ref="calendar"
            :categories="selectedCategories"
            :categoriesLength="offer.offerCategories.length"
            :calendarEventId="calendarEventId"
            :bottomEvents="calendarSlots"
            :showSoldOut="showSoldOut && !isLoadingSlots"
            :showBlinkingArrows="showBlinkingArrows"
            :labelForSoldOut="labelForSoldOut"
            :innerWidth="innerWidth"
            :offer="offer"
            :innerHeight="innerHeight"
            :showShare="!calendarEventId"
            :showFreeSchedule="showFreeSchedule"
            :regosIframe="regosIframe"
            @date-change="dateChange"
            @modal-date-change="modalDateChange"
            @unselect-category="onUnselectCategory"
            @free-schedule-change="onFreeScheduleChange"
            @open-custom-reservation-modal="openCustomReservatinModal"
        >
            <template #left-side>
                <user-select
                    ref="userSelect"
                    :users="offerPerformers"
                    :showArrows="showSoldOut && !isLoadingSlots"
                    valuePath="availabilityOfferId"
                    @user-selected="availabilityOfferSelected"
                ></user-select>
            </template>
            <template #dropdown>
                <time-slots-selector
                    :selcetedDaySlots="selectedDaySlots"
                    :timeZone="offer?.location?.timeZone"
                    :title="$t('components.availability.check_availability.select_time')"
                    @time-selected="timeSelected"
                ></time-slots-selector>
            </template>
        </common-calendar>
        <confirm-reservation-modal
            v-if="isReservationModalOpen"
            :offer="offer"
            :categories="selectedCategories"
            :time="selectedTime"
            :date="selectedDate"
            :moderatorNote="moderatorNote"
            :availabilityOfferId="availabilityOfferId"
            :calendarEventId="calendarEventId"
            :freeSchedule="freeSchedule"
            @close="closeReservationModal"
            @reservation-confirmed="loadCalendarSlots(dateFrom, dateTo, availabilityOfferId)"
            @reservation-failed="loadCalendarSlots(dateFrom, dateTo, availabilityOfferId), closeReservationModal()"
        ></confirm-reservation-modal>
        <custom-reservation-modal
            v-if="isCustomReservationModalOpen"
            :offer="offer"
            :categories="selectedCategories"
            :availabilityOfferId="availabilityOfferId"
            :calendarEventId="calendarEventId"
            :moderatorNoteProp="moderatorNote"
            @close="closeCustomReservatinModal"
            @open="openCustomReservatinModal"
        ></custom-reservation-modal>
        <common-modal
            v-if="isChooseTimeslotModalOpen"
            :showCloseIcon="true"
            width="wide"
            height="medium"
            @close="closeChooseTimeslotModal"
        >
            <time-slots-selector
                class="choose-timeslot-modal__selector"
                :selcetedDaySlots="selectedDaySlots"
                :timeZone="offer?.location?.timeZone"
                :title="$t('components.availability.check_availability.select_time')"
                @time-selected="timeSelected"
            ></time-slots-selector>
        </common-modal>
        <profile-not-completed-modal
            :isModalOpen="isProfileNotCompletedModalOpen"
            @close-complete-profile-modal="closeProfileNotCompletedModal"
        ></profile-not-completed-modal>
        <please-register-modal v-if="isPleaseRegisterModalOpen" @close="closePleaseRegisterModal"></please-register-modal>
    </div>
</template>

<script>
    import AvailabilityUserRoleEnum from 'enums/availability_user_role.enum'
    import UserSelect from 'components/page-elements/user/UserSelect'
    import ConfirmReservationModal from 'components/modals/offer/ConfirmReservationModal'
    import TimeSlotsSelector from 'components/page-elements/availability/TimeSlotsSelector'
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import dayjs from 'dayjs'
    import PleaseRegisterModal from 'components/modals/PleaseRegisterModal'
    import ProfileNotCompletedModal from 'components/modals/user/ProfileNotCompletedModal'
    import PriceMixin from 'mixins/PriceMixin'
    import OfferCategoryComponent from 'components/page-elements/offer/OfferCategoryComponent'
    import RoutesEnum from 'enums/routes.enum'
    import CalendarEventEnums from 'enums/calendar_event.enum'
    import CustomReservationModal from 'components/modals/offer/CustomReservationModal'

    export default {
        mixins: [PriceMixin],
        components: {
            UserSelect,
            OfferCategoryComponent,
            TimeSlotsSelector,
            ConfirmReservationModal,
            PleaseRegisterModal,
            ProfileNotCompletedModal,
            CustomReservationModal,
        },

        props: {
            offer: { type: Object, default: undefined },
            categories: { type: Object, default: undefined },
            calendarEventId: { type: [Number, String], default: undefined },
            moderatorNote: { type: String, default: undefined },
            reservation: { type: Object, default: undefined },
        },

        data: () => ({
            dateFrom: dayjs.utc().startOf('month').format('YYYY-MM-DD'),
            dateTo: dayjs.utc().endOf('month').format('YYYY-MM-DD'),
            availabilityOfferId: null,
            slots: [],
            selectedDaySlots: null,
            selectedTime: null,
            selectedDate: null,
            isReservationModalOpen: false,
            isProfileNotCompletedModalOpen: false,
            isPleaseRegisterModalOpen: false,
            haveEventsLoaded: false,
            innerWidth: 0,
            innerHeight: 0,
            timeZone: 'Europe/Belgrade',
            isLoadingSlots: false,
            selectedCategories: [],
            isChooseTimeslotModalOpen: false,
            isCategoriesSelected: false,
            freeSchedule: false,
            offerPerformers: [],
            isCustomReservationModalOpen: false,
        }),

        computed: {
            showFreeSchedule() {
                if (!this.$auth.isLoggedIn() || !this.availabilityOfferId) return false
                return this.offer?.availabilityOffers
                    .find((ao) => ao.id === this.availabilityOfferId)
                    .availability?.availabilityUsers.some((au) => au.user.id === this.$auth.getUser().id)
            },

            regosIframe() {
                return this.offer?.availabilityOffers?.find((ao) => ao.id === this.availabilityOfferId)?.availability?.regosIframe
            },

            calendarSlots() {
                return {
                    titlePath: 'common.calendar.slots',
                    dates: this.slots?.map((slot) => ({ date: slot.date, count: slot.slots.length })),
                }
            },

            showBlinkingArrows() {
                return (
                    this.haveEventsLoaded &&
                    !this.calendarSlots?.dates?.reduce((acc, val) => {
                        acc += val.count
                        return acc
                    }, 0)
                )
            },

            showSoldOut() {
                return !this.calendarSlots?.dates?.reduce((acc, val) => {
                    acc += val.count
                    return acc
                }, 0)
            },

            labelForSoldOut() {
                if (!this.showSoldOut) return ''
                if (this.dateTo > dayjs().format('YYYY-MM-DD')) {
                    return this.$t('common.microcopy.unavailable')
                } else {
                    return this.$t('common.microcopy.soldout')
                }
            },
        },

        methods: {
            availabilityOfferSelected({ availabilityOfferId }) {
                this.freeSchedule = false
                this.availabilityOfferId = availabilityOfferId
                this.loadCalendarSlots(this.dateFrom, this.dateTo, availabilityOfferId)
                const query = {
                    ...this.$route.query,
                    selectedUserId: availabilityOfferId,
                    selectedCategoriesIds: this.selectedCategories.map((item) => item.id),
                }

                if (this.calendarEventId) {
                    this.$router.push({
                        name: RoutesEnum.RESERVATIONS.RESCHEDULE,
                        query: {
                            ...this.$route.query,
                            selectedUserId: availabilityOfferId,
                        },
                    })
                    return
                }
                this.$router.push({
                    name: RoutesEnum.OFFER.SINGLE,
                    params: { id: this.offer.id, offerType: 'availability' },
                    query,
                })
            },

            getOfferPerformers(categories) {
                let selectedCategories = []
                const categoriesQuery = this.$route.query?.selectedCategoriesIds || this.$route.query?.categoryIds
                if (categories) {
                    selectedCategories = categories.map((ct) => ct.id)
                } else if (categoriesQuery) {
                    selectedCategories = (Array.isArray(categoriesQuery) ? categoriesQuery : [categoriesQuery]).map((cat) =>
                        Number(cat),
                    )
                }

                const excludeUsers = this.offer.offerCategories
                    .filter((oc) => selectedCategories.includes(oc.id))
                    .map((oc) => oc.excludeUsers || [])
                    .flat()

                this.offerPerformers = (
                    this.offer?.availabilityOffers?.map((availabilityOffer) => ({
                        availabilityOfferId: availabilityOffer.id,
                        availabilityTitle: availabilityOffer?.availability?.titleTextContent,
                        ...availabilityOffer.availability.availabilityUsers.find(
                            (availabilityUser) => availabilityUser.function === AvailabilityUserRoleEnum.PERFORMER,
                        ).user,
                    })) || []
                ).filter((user) => !excludeUsers.includes(user.id))
            },

            closeProfileNotCompletedModal() {
                this.isProfileNotCompletedModalOpen = false
            },

            onCategoriesSelect(categories) {
                this.isCategoriesSelected = true
                this.selectedCategories = categories
            },

            onCategoriesChange(categories) {
                this.getOfferPerformers(categories)
            },

            onUnselectCategory() {
                this.selectedCategories = undefined
                this.$router.push({ name: RoutesEnum.OFFER.SINGLE, params: { id: this.offer.id, offerType: 'availability' } })
            },

            loadCalendarSlots(dateFrom, dateTo, availabilityOfferId) {
                this.isLoadingSlots = true
                this.$startLoader()
                if (this.$auth.getUser()?.id) {
                    return CalendarEventAPI.getFreeSlotsWithAuth({
                        dateFrom,
                        dateTo,
                        availabilityOfferId,
                        offerCategoryIds: this.selectedCategories.map((item) => item.id),
                        freeSchedule: this.freeSchedule,
                        rescheduleCalendarEventId: this.calendarEventId ? Number(this.calendarEventId) : undefined,
                    })
                        .then((res) => {
                            this.slots = res.freeSlots
                            this.timeZone = res.timeZone
                        })
                        .catch((err) => {
                            if (err.error.message === CalendarEventEnums.ERRORS.NOT_PERMISSIONED) {
                                this.$refs.calendar.freeSchedule = false
                                this.$toast.error(this.$t('components.reservations.errors.not_permissioned'))
                            }
                        })
                        .finally(() => {
                            this.haveEventsLoaded = true
                            this.isLoadingSlots = false
                            this.$stopLoader()
                        })
                } else {
                    return CalendarEventAPI.getFreeSlots({
                        dateFrom,
                        dateTo,
                        availabilityOfferId,
                        offerCategoryIds: this.selectedCategories.map((item) => item.id),
                    })
                        .then((res) => {
                            this.slots = res.freeSlots
                            this.timeZone = res.timeZone
                        })
                        .finally(() => {
                            this.haveEventsLoaded = true
                            this.isLoadingSlots = false
                            this.$stopLoader()
                        })
                }
            },

            onFreeScheduleChange(payload) {
                this.freeSchedule = payload
                this.loadCalendarSlots(this.dateFrom, this.dateTo, this.availabilityOfferId, this.freeSchedule)
            },

            dateChange({ dateFrom, dateTo }) {
                this.dateFrom = dateFrom
                this.dateTo = dateTo
                if (this.availabilityOfferId && !this.isLoadingSlots) {
                    this.loadCalendarSlots(this.dateFrom, this.dateTo, this.availabilityOfferId)
                }
            },

            modalDateChange(payload) {
                if (!payload.date) return
                if (this.isReservationModalOpen) return
                this.isChooseTimeslotModalOpen = true
                this.selectedDate = payload.date
                this.selectedDaySlots = payload.date ? this.slots.find((slot) => slot.date === payload.date) : null
            },

            timeSelected(time) {
                this.closeChooseTimeslotModal()
                if (!this.$auth.getUser()?.id) {
                    this.isPleaseRegisterModalOpen = true
                    return
                } else if (!this.$auth.isUserCompleteProfile()) {
                    this.isProfileNotCompletedModalOpen = true
                    return
                }
                this.selectedTime = time
                this.openReservationModal()
            },

            closeReservationModal() {
                this.isReservationModalOpen = false
                this.$refs.calendar.closeDayModal()
            },

            closeChooseTimeslotModal() {
                this.isChooseTimeslotModalOpen = false
            },

            openReservationModal() {
                this.isReservationModalOpen = true
            },

            openCustomReservatinModal() {
                this.isCustomReservationModalOpen = true
            },

            closeCustomReservatinModal() {
                this.isCustomReservationModalOpen = false
            },

            closePleaseRegisterModal() {
                this.isPleaseRegisterModalOpen = false
            },

            setBounderis() {
                const el = document.getElementsByClassName('calendar')[0]
                if (el) {
                    this.innerWidth = el.clientWidth + 100
                }
            },

            onPopState() {
                this.getOfferPerformers()

                if (!this.$route.query?.selectedCategoriesIds && !this.$route.query.selectedUserId) {
                    if (
                        this.offer?.offerCategories?.length === 1 &&
                        window.history.state.back &&
                        window.history.state.current !== '/offer/find'
                    ) {
                        window.history.back()
                    } else {
                        this.selectedCategories = undefined
                        this.isCategoriesSelected = false
                    }
                } else {
                    if (!this.calendarEventId) {
                        const selectedCategoriesIds = this.$route.query?.selectedCategoriesIds
                        const selectedCategories = Array.isArray(selectedCategoriesIds)
                            ? selectedCategoriesIds
                            : [selectedCategoriesIds]
                        this.selectedCategories = this.offer?.offerCategories?.filter((category) =>
                            selectedCategories?.includes(category.id.toString()),
                        )
                    }
                }
            },
        },

        watch: {
            categories() {
                if (this.categories.length) {
                    this.selectedCategories = this.categories
                }
            },
        },

        beforeUnmount() {
            window.removeEventListener('popstate', this.onPopState)
        },

        mounted() {
            this.getOfferPerformers()
            window.addEventListener('popstate', this.onPopState)

            this.setBounderis()
            if (this.offer?.offerCategories?.length === 1) {
                this.selectedCategories = this.offer.offerCategories
            }
            if (this.categories) {
                this.selectedCategories = this.categories
            }
            const selectedCategoriesIds = this.$route.query?.selectedCategoriesIds

            if (selectedCategoriesIds?.length) {
                if (typeof selectedCategoriesIds === 'string') {
                    this.selectedCategories = this.offer.offerCategories.filter((item) => {
                        if (Number(selectedCategoriesIds) === Number(item.id)) {
                            item.isSelected = true
                            return true
                        }
                    })
                    return
                }

                this.selectedCategories = this.offer.offerCategories.filter((item) => {
                    if (selectedCategoriesIds.map((item) => Number(item)).includes(item.id)) {
                        item.isSelected = true
                        return true
                    }
                })
            }
        },
    }
</script>

<style scoped lang="scss">
    .choose-timeslot-modal__selector {
        width: 100%;
        height: calc(100% - 150px);
    }
</style>
